import { useContext } from 'react';
import { useFlags, useFlagsmith } from '@flagsmith/flagsmith/react';
import { FeatureFlagsReadyContext } from './featureFlagsContext';
import type { FeatureFlagKey } from './featureFlagKeys';

/**
 * Is a single feature on for the signed-in user?
 *
 *   const isFoldersEnabled = useFeatureFlag('tx_folders');
 *
 * Returns false until flags have loaded, and stays false if Flagsmith is unreachable, so a
 * feature is never shown by accident.
 *
 * Pass `undefined` for "there is no flag to check", which `PrivateRoute` needs for routes
 * that are not flag gated - hooks cannot be called conditionally. Keep the key stable
 * across renders: Flagsmith subscribes using the key from the first render only.
 */
export function useFeatureFlag(key: FeatureFlagKey | undefined): boolean {
  const flagsmith = useFlagsmith();

  // `useFlags` is what subscribes this component to the flag, so it re-renders when the
  // real values arrive. We ignore its return value and ask `hasFeature` instead, because
  // that is Flagsmith's own definition of "on" rather than our guess at it.
  useFlags(key ? [key] : []);

  if (!key) return false;

  // `useFlags` already recorded an evaluation of this key, so skip analytics here to avoid
  // counting the same read twice.
  return flagsmith.hasFeature(key, { skipAnalytics: true });
}

/**
 * Same as `useFeatureFlag`, for components that gate on several flags at once:
 *
 *   const { tx_folders: isFoldersEnabled } = useFeatureFlags(['tx_folders']);
 *
 * Pass a literal array. Flagsmith subscribes to the keys from the first render only, so a
 * list that changes between renders would keep listening to the original keys.
 */
export function useFeatureFlags<K extends FeatureFlagKey>(keys: readonly K[]): Record<K, boolean> {
  const flagsmith = useFlagsmith();
  useFlags(keys);

  const enabledByKey = {} as Record<K, boolean>;
  keys.forEach((key) => {
    enabledByKey[key] = flagsmith.hasFeature(key, { skipAnalytics: true });
  });
  return enabledByKey;
}

/**
 * Have the flags for the signed-in user settled yet? See `FeatureFlagsReadyContext` for
 * when you need this - most components do not.
 */
export function useFeatureFlagsReady(): boolean {
  return useContext(FeatureFlagsReadyContext);
}
